import React, { useCallback } from 'react';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { useRoute, type RouteProp } from '@react-navigation/native';
import { HomeTabScreens } from '../Navigator';
import { TabWebViewScreen } from '../screens/TabWebView';
import { BottomTabBar } from 'shared/src/component';
import {
  useInstanceId,
  useMount,
  useNativeEvent,
  useOpenDeeplink,
} from 'shared/src/hooks';
import { useConfigs, useMainTabs } from 'shared/src/queries';
import { localStorageBridge } from 'shared/src/utils/bridge';
import { isWeb } from 'shared/src/utils';

// 하단탭은 mainTabs(API) 응답으로 동적 등록된다. WEBVIEW 탭은 자신의 route name
// (=screen_name)으로 mainTabs 에서 설정을 조회하므로 라우트 파라미터를 쓰지 않는다.
type BottomTabParamList = Record<string, undefined>;

const BottomTabs = createBottomTabNavigator<BottomTabParamList>();

// 이 컴포넌트는 네이티브 Stack('Home')·웹 Stack('tab') 양쪽에서 재사용되므로 특정 라우트에
// 고정된 props 를 받지 않고, 필요한 param(incomingDeeplink)만 useRoute 로 읽는다.
type IncomingDeeplinkRoute = RouteProp<
  { screen: { incomingDeeplink?: string } },
  'screen'
>;

export const BottomTabNavigator = () => {
  const route = useRoute<IncomingDeeplinkRoute>();
  const instanceId = useInstanceId();
  const { data: configs } = useConfigs();
  const { data: mainTabs } = useMainTabs();

  const { openDeeplink } = useOpenDeeplink();

  const handleIncomingDeeplink = useCallback((url: string) => {
    console.log('[incomingDeeplink]', url);
  }, []);

  useMount(() => {
    const initialDeeplink = route.params?.incomingDeeplink;
    if (initialDeeplink) {
      handleIncomingDeeplink(initialDeeplink);
      return;
    }

    void (async () => {
      if (isWeb) return;

      const shouldShow =
        (await localStorageBridge.getBoolean('shouldShowOnboarding')) ?? true;
      if (!shouldShow) return;
      const onboardingDeeplink = configs?.onboarding_deeplink;
      if (!onboardingDeeplink) return;
      openDeeplink(onboardingDeeplink);
      localStorageBridge.putBoolean('shouldShowOnboarding', false);
    })();
  });

  useNativeEvent('incomingDeeplink', instanceId, data => {
    handleIncomingDeeplink(data.incomingDeeplink);
  });

  // 서버가 지정한 초기 선택 탭(selected_id, id 기준)을 route name(screen_name)으로 매핑.
  // 매칭 실패 시 undefined → 첫 번째 탭이 기본 선택된다.
  const initialRouteName = mainTabs.item_list.find(
    tab => tab.id === mainTabs.selected_id,
  )?.screen_name;

  return (
    <BottomTabs.Navigator
      initialRouteName={initialRouteName}
      screenOptions={{ headerShown: false }}
      tabBar={props => <BottomTabBar {...props} />}
    >
      {/* 라우트를 mainTabs(API) 순서/구성으로 등록한다. BottomTabBar 는 mainTabs.item_list
          를 인덱스로 route 에 매핑하므로, 여기 등록 순서가 그대로 정합돼야 한다.
          - CUSTOM: screen_name 으로 HomeTabScreens 의 네이티브 컴포넌트를 매칭
          - WEBVIEW: 공용 TabWebViewScreen 에 default_query.url 을 넘겨 렌더 */}
      {mainTabs.item_list.map(tab => {
        const matched = HomeTabScreens.find(
          screen => screen.screenName === tab.screen_name,
        );
        const component =
          tab.type === 'WEBVIEW' ? TabWebViewScreen : matched?.component;

        // 매칭되는 화면이 없으면 등록하지 않는다.
        if (!component) return null;

        return (
          <BottomTabs.Screen
            key={tab.screen_name}
            name={tab.screen_name}
            component={component}
            options={{
              title: `${tab.id !== 'home' ? `${tab.name} - ` : ''}에이블리 | 전 상품 무료배송`,
            }}
          />
        );
      })}
    </BottomTabs.Navigator>
  );
};
